// ⓘ explanations for the Product Feeds pages, kept in one place so the same
// term reads the same everywhere. Keys match the config values they describe.

/**
 * Looks up a config value's explanation. Values come from user config, so a
 * name such as "constructor" must not find Object.prototype members.
 */
export const explain = (map, key) => (map && Object.prototype.hasOwnProperty.call(map, key) ? map[key] : undefined);

export const GLOSSARY = {
    tabs: {
        sources: "The APIs this feed reads, in priority order. Each source has a request, a structure (where products and variants are), field mappings and a role.",
        fields: "The feed model: the product and variant fields every source maps onto, what each field means (its role) and how values from several sources combine.",
        columns: "The CSV file Target imports: Target's reserved entity.* columns plus your custom attributes, and how each is filled.",
        preview: "Builds the saved draft without publishing it, so you can check rows, sources and issues first.",
        schedule: "How the feed reaches Target, what happens to products with 0 inventory, how often it's rebuilt, guardrails, file options and alerts.",
        runs: "Every build: what started it, its outcome and its Target push.",
    },

    sourceSteps: {
        request: "Where and how to call the API: URL, parameters, headers, pagination and dependent calls.",
        structure: "Where the products and variants are in the response, and how each variant belongs to its product.",
        mapping: "Which value in the response fills each feed field.",
        role: "Whether this source creates products or only adds to them, and what happens when it fails.",
    },

    feedStatus: {
        published: "The configuration version builds use. Each publish increases the version.",
        notPublished: "Nothing is built until the feed is published.",
        unpublished: "The draft differs from the published configuration. Builds keep using the published version until you publish.",
        unsaved: "Edits on this page that haven't been saved to the draft yet.",
        paused: "Paused feeds aren't rebuilt or pushed.",
        lastRun: "The outcome of the most recent build.",
        running: "A build or Adobe Target push is in progress. The page updates by itself when it finishes, and the result appears in the Runs tab.",
        saveDraft: "Stores your edits without affecting builds.",
        publish: "Checks the draft (and the Target connection for API delivery), makes it the configuration builds use and starts a build in the background. If that build fails or is held, nothing is pushed and Target keeps its products.",
        discard: "Replaces the draft with the published configuration.",
        draftChecks: "Problems found when the draft was saved. Errors must be fixed before publishing; warnings are advisory.",
    },

    sources: {
        roles: {
            primary: "Creates products. Every product in the feed comes from at least one primary source.",
            enrichment: "Only adds fields and variants to products a primary source already created. Records that don't match are reported, not added.",
        },
        optional: "If this source fails, the build continues without it.",
        required: "If this source fails, the build stops and nothing is pushed, so Target keeps what it has.",
        dependsOn: "This source runs after these sources because it uses their results (dependent calls or lookups).",
        forEach: "One request is made per value taken from another source's records; the value replaces {{each}}.",
        mapped: "How many of the feed model's fields this source fills. Fields it doesn't map can come from other sources.",
        matchOn: {
            "product.key": "Records are matched to products by product key.",
            "variant.key": "Each record's variants are matched to existing variants by variant key. Variants that aren't already in the feed are ignored.",
            "variant.field": "Variants are matched on a variant field, e.g. a stock API keyed by SKU.",
        },
        order: "Sources are read top to bottom. With the default merge policy, the first source that has a value for a field wins.",
        presets: "The starting structure for a new source. You can change it in the source's Structure step.",
    },

    relations: {
        contained: "Variants sit inside each product in the response, e.g. products[*].variants[*].",
        key: "Products and variants are separate lists, linked by a product id on each variant (or a list of variant ids on each product).",
        group: "The response is a flat list of variant rows; rows that share a value (e.g. a parent id) become one product.",
        self: "Each product is its own single variant, for catalogues without variants.",
        zip: "Variants are built from parallel arrays on the product: position 1 of each array is variant 1, and so on.",
        cartesian: "Every combination of the product's option lists becomes a variant, e.g. colours × sizes.",
    },

    request: {
        method: "GET for most APIs. POST sends the body below, e.g. for GraphQL or batch lookups.",
        url: "Sent exactly as entered, including its query string. Placeholders such as {{page}} or {{each}} are filled in for each call.",
        format: "json: one JSON document per response. ndjson: one JSON object per line.",
        query: "The URL's query string as a table. Secret parameters are kept out of the URL, stored encrypted and added when the API is called.",
        headers: "Sent with every call, e.g. API keys or Accept. Secret headers are stored encrypted and never shown again.",
        body: "For POST requests. Placeholders become JSON values, so {{each}} with a batch becomes a JSON array.",
        repeatFor: "Makes the same request once per value (e.g. each category or locale) and combines the results. Use {{repeat}} where the value goes.",
        pagination: "How to fetch further pages. Each page is processed like a separate response.",
        paginationTypes: {
            none: "A single request.",
            page: "Page numbers (page=1, 2, …) until a page is empty or shorter than the page size.",
            offset: "Offsets (offset=0, 50, …) until a page is empty or shorter than the limit.",
            cursor: "Each response gives a cursor that is sent with the next request.",
            next_url: "Each response contains the URL of the next page.",
            link_header: "The next page's URL is in the HTTP Link header.",
        },
        dependent: "Calls this API once per value found in another source's records, e.g. a details call per product. The other source runs first.",
        forEachSource: "The source whose mapped records supply the values.",
        forEachValue: "Which value to send: $product.<field> or $variant.<field> of that source's mapped fields. Repeated values are sent once.",
        batchSize: "How many values go into each call. Above 1, {{each}} becomes a list (joined with the separator in URLs).",
        batchJoin: "What separates batched values in a URL, e.g. a,b,c.",
        concurrency: "How many calls run at the same time (up to 8). Higher is faster but puts more load on the API.",
        maxCalls: "An upper limit on calls per build. Values beyond it are left out and the source is reported as truncated.",
        onError: {
            fail: "Any failed call fails the whole source.",
            skip: "A failed call is skipped and the build continues with the other values, as long as failures stay under the allowed share.",
        },
        maxFailedPct: "If more than this share of dependent calls fail, the whole source fails. Skipped calls are listed in the preview.",
        timeout: "How long to wait for each response before it counts as failed (default 30 seconds).",
        maxBytes: "Responses larger than this are rejected (default 25 MB).",
        retries: "How many times to retry timeouts, HTTP 429 and 5xx responses. Other errors aren't retried.",
        backoff: "Wait before the first retry. It doubles for each retry, unless the API sends Retry-After.",
        test: "Calls the API (a few calls at most) so you can explore the response and get structure suggestions.",
    },

    structure: {
        suggestions: "Product lists found in the test response, best match first. 'Use' applies the paths; 'Use + auto-map' also maps fields whose names suit each role.",
        prepare: "Steps that reshape each response before products are extracted, e.g. to parse JSON stored as text.",
        prepareOps: {
            parse_embedded_json: "Turns JSON stored as a text value into real objects so it can be mapped.",
            pivot: "Turns a list of name/value pairs into an object, e.g. [{name: colour, value: red}] → {colour: red}.",
            force_array: "Wraps a single object in a list, for APIs that drop the array when there's only one item.",
            rename: "Renames a key on the selected objects.",
            drop: "Removes the selected nodes, e.g. large blocks the feed doesn't need.",
        },
        relation: "How each variant belongs to its product.",
        steps: "Each level selects nodes with a JSONPath and gives them a name (as) that mappings can read. A level can start from an earlier level (from).",
        from: "Where the selection starts: the whole document, or the nodes of an earlier level.",
        select: "JSONPath of the nodes: $ is the document, @ is the node named in 'from'. [*] means every item of a list.",
        as: "Name for these nodes in mappings, e.g. product lets you read $product.brand.",
        rows: "The JSONPath of the flat list of variant rows.",
        groupBy: "Rows with the same value here become one product.",
        variantProductId: "The value on each variant that points to its product.",
        productId: "The product value that variants point to.",
        productList: "A list on the product of the ids of its variants.",
        zipArrays: "Name → path of each parallel array. Position 1 of every array becomes variant 1.",
        zipMismatch: "What to do when the arrays have different lengths.",
        axes: "Name → path of each option list. Every combination becomes a variant.",
        maxCombinations: "Limits the number of variants a product can get from combinations.",
        where: "Keeps only products that meet a condition; the others are counted as filtered.",
        check: "Runs the extraction on the tested response and shows what was found.",
        explorer: "The tested response. Use a pick button, then click a node to fill in its path.",
        pick: "Fill this path by clicking a node in the response explorer.",
    },

    mapping: {
        bindings: "Paths without a prefix read from the current product (or variant). $product, $variant, $root (the whole response), $key and named levels read from elsewhere.",
        expression: "How the field's value is found. Transforms after it clean up or convert the value.",
        modes: {
            path: "Reads the value at a path. Add fallback paths: the first one with a value wins.",
            const: "The same value for every record.",
            template: "Text combining values, e.g. {{$product.brand}} {{color}}.",
            ref: "A value from the call itself: the node's key or position, the product key, or the request's repeat, each, page or URL.",
            all: "true only if every listed path (or condition) is true, e.g. active and in the catalogue.",
            any: "true if at least one listed path (or condition) is true.",
            advanced: "JSON for lookups in the same response, lookups in another source (source_lookup) and case expressions.",
        },
        refs: {
            $key: "The key of the current node, for responses keyed by id.",
            $index: "The node's position in its list.",
            $product_key: "The product's key, in variant mappings.",
            "$source.repeat": "The repeat value of the request.",
            "$source.each": "The dependent-call value of the request.",
            "$source.page": "The page number of the request.",
            "$source.url": "The URL that was requested.",
        },
        foundIn: "How many of the extracted products (or variants) have a value for this field.",
        samples: "Values from the first extracted records.",
        autoMapped: "Filled in by auto-map. Check it matches what you expect.",
        typeErrors: "Values that couldn't be converted to the field's type, per field. Those values are left empty; add a transform (e.g. to_number) or change the type.",
        addTransform: "Adds a step that converts or cleans the value. Transforms run in order.",
        secret: "Secret parameters are kept out of the URL, stored encrypted and added when the API is called. Saved secrets can't be shown again.",
        needsMapping: "The feed can't be published until a source maps this role.",
        autoMap: "Maps unmapped fields by role, using key names found in the detected structure (e.g. title for the name role).",
        loadSamples: "Extracts records from the tested response to show sample values and the sample context.",
        pickValue: "Map this field by clicking a value in the sample context.",
        fallback: "Add another path to try when the ones before it are empty.",
        context: "The data one sample product can read. Top-level names are bindings: $product, $variant and named levels.",
        addField: "Adds a field to the feed model so every source can map it.",
    },

    fields: {
        model: "The fields every source maps onto. Columns and rules use these names and roles, never the source's own keys.",
        roles: {
            key: "The unique id. The product key becomes entity.id and must match the id your site sends to Target. Variant keys identify variants.",
            name: "The product name, used for entity.name. Required.",
            price: "The variant price. entity.value is the lowest price under the feed's price rule.",
            availability: "Whether a variant can be bought. Only available variants count towards inventory; a product with none has inventory 0.",
            image: "An image URL, used for entity.thumbnailUrl.",
            url: "A page URL, used for entity.pageUrl.",
            category: "The category or categories, used for entity.categoryId.",
            brand: "The brand, used for entity.brand.",
            default_variant_ref: "On a product: which variant is the default. Used by the representative 'match' rule.",
            default_variant_match: "On a variant: the value compared with the product's default variant ref.",
        },
        types: {
            string: "Text.",
            number: "A decimal number. Text such as 1.299,90 is converted.",
            integer: "A whole number; decimals are dropped.",
            boolean: "true or false. yes/no, 1/0 and similar are converted.",
            date: "A date and time, stored in ISO 8601 format.",
            "string[]": "A list of text values.",
            "number[]": "A list of numbers.",
            object: "Any JSON value, kept as it is.",
        },
        merge: {
            priority: "The first source (in the Sources order) with a value wins.",
            union: "Values from every source are combined into one list without duplicates.",
            min: "The lowest value across sources.",
            max: "The highest value across sources.",
            any: "true if any source says true.",
            all: "true only if every source with a value says true, e.g. active in the catalogue and in stock.",
            sum: "Values from every source are added together.",
        },
        coverage: "Which sources map this field. ✓ means the source fills it.",
        rowLevel: "Product: one row per product, with variant details in list, numbered or JSON columns. Variant: one row per variant, with variant keys as entity.id.",
        variantOrder: "The order of variants in list, numbered and JSON columns, and for the 'first' rules.",
        requireVariants: "Products that end up with no variants are left out of the file and reported.",
        representative: "One variant chosen per product for single-variant values, such as the thumbnail when the product has no image. Rules are tried in order; the first that finds a variant wins.",
        representativeRules: {
            match: "The variant the product names as its default (default variant ref and match roles).",
            min: "The variant with the lowest value of the chosen field or role.",
            max: "The variant with the highest value of the chosen field or role.",
            first: "The first variant in variant order.",
        },
    },

    variantFilters: {
        all: "Every variant.",
        available: "Only variants whose availability is true.",
        unavailable: "Only variants whose availability is false.",
        custom: "Variants that meet a condition set in the JSON.",
    },

    columns: {
        reserved: "Attributes Target defines. id and name are required; value is the price and inventory decides whether a product can be recommended.",
        custom: "Your own attributes (entity.<name>) for criteria, inclusion rules and designs. Target allows up to 100.",
        count: "Target allows 100 custom attributes. Numbered columns count once per slot.",
        reservedColumns: {
            id: "Must match the product id your site sends to Target on product pages.",
            name: "The product name shown in designs. Rows without one are dropped.",
            categoryId: "Comma-separated categories, up to 250 characters.",
            message: "Free text for designs.",
            thumbnailUrl: "The image shown in designs.",
            value: "The price. It follows the feed's price rule and can't be edited otherwise.",
            pageUrl: "The product page link.",
            inventory: "A stock indicator. With Target's 'inventory > 0' inclusion rule, products at 0 aren't recommended.",
            margin: "Profit margin, for margin-based rules.",
        },
        kinds: {
            product: "A product field (or the field with a role), as it is.",
            aggregate: "One value calculated across the product's variants, e.g. the highest price or all colours.",
            list: "One value per variant as a list, e.g. [\"S\",\"M\"]. Suited to Velocity designs.",
            indexed: "One column per variant (name1…nameN), for designs that can't read lists.",
            variants_json: "The variants as a JSON array, for designs that parse JSON.",
            representative: "A field from one chosen variant: the representative variant or the price variant.",
            template: "Text built from product and representative-variant values.",
            const: "The same value on every row, or a blank column.",
            fallback: "The first of several column definitions that gives a value.",
        },
        aggregateOps: {
            min: "The lowest value.",
            max: "The highest value.",
            sum: "All values added together.",
            avg: "The average value.",
            count: "The number of variants (after the variant filter).",
            count_true: "The number of variants where the field is true.",
            any: "true if any variant's value is true.",
            all: "true if every variant's value is true.",
            distinct: "Every different value, as a list.",
            first: "The first value found.",
        },
        valueFrom: "The field (or the field with a role) the column reads.",
        onLimit: {
            shorten: "Cut the value down to Target's limit.",
            skip_value: "Leave the value out.",
            fail_row: "Drop the whole row and report it.",
            drop_fields: "Remove fields from the end of the field list, then variants, until it fits.",
            truncate_variants: "Remove variants from the end until it fits.",
        },
        nullAs: "Target rejects lists that mix numbers and blanks, so blank values are replaced with this.",
        dedupe: "Yes lists each value once; No keeps one value per variant, in variant order.",
        count_slots: "How many numbered columns to create; variants beyond this are left out.",
        jsonFields: "Which variant fields go into each JSON object, as output name = field. $available adds 1 or 0 for availability.",
        template: "Use {{$product.<field>}}, {{$product.key}} and {{$representative.<field>}}.",
        constValue: "Leave empty for a blank column.",
        transforms: "Run in order on the column's value, e.g. round prices or turn categories into slugs.",
        required: "Rows where this column is empty are dropped and reported.",
        purpose: "Starts the column with the kind that suits how you'll use it. You can change everything afterwards.",
    },

    priceVariant: "The variant entity.value comes from. Columns can read other values from the same variant, e.g. its loyalty price.",
    representativeVariant: "The variant chosen by the representative rules on the Fields tab.",

    transforms: {
        to_number: "Converts text to a number. Optional locale sets the decimal separator, e.g. pt-PT for 1.299,90.",
        to_integer: "Converts to a whole number, dropping decimals.",
        to_boolean: "Converts yes/no, 1/0 and similar to true or false. Optional truthy lists the values that mean true.",
        bool_to_int: "Converts true/false to 1/0.",
        to_iso_date: "Converts a date to ISO 8601. Optional format describes the input, e.g. DD/MM/YYYY.",
        parse_json: "Parses JSON stored as text.",
        trim: "Removes spaces at the start and end.",
        lower: "Converts to lower case.",
        upper: "Converts to upper case.",
        slug: "Converts to a URL-style slug, e.g. Men's Shoes → mens-shoes.",
        strip_html: "Removes HTML tags and decodes common entities.",
        truncate: "Shortens text to max characters.",
        replace: "Replaces matches of a regular expression (pattern) with 'with'.",
        map_values: "Swaps values using a JSON map, e.g. {\"Y\": true}. Optional default is used for unmapped values.",
        null_if_match: "Empties the value when it matches the pattern, e.g. placeholder images.",
        prefix: "Adds text in front of the value.",
        strip_prefix: "Removes text from the start of the value.",
        suffix: "Adds text after the value, e.g. ?width=600 on an image URL.",
        url_join: "Makes a relative URL absolute using base.",
        round: "Rounds to places decimals (default 2).",
        multiply: "Multiplies by n.",
        divide: "Divides by n.",
        first: "The first non-empty item of a list.",
        last: "The last non-empty item of a list.",
        nth: "The item at position n of a list (0 is the first).",
        dedupe: "Removes repeated items from a list.",
        min: "The lowest number in a list.",
        max: "The highest number in a list.",
        sum: "The total of a list of numbers.",
        count: "The number of non-empty items.",
        join: "Joins a list into text with sep (default a comma).",
        split: "Splits text into a list at sep (default a comma).",
        sort: "Sorts a list; dir desc sorts in reverse.",
        limit: "Keeps the first n items of a list.",
        flatten: "Turns nested lists into one list.",
        compact: "Removes empty items from a list.",
        truncate_list: "Keeps as many items as fit in max_chars when joined.",
        default: "Uses value when the value is empty.",
    },

    outcomes: {
        published: "The feed was built and, for API delivery, pushed to Target (for a preview: would be).",
        held: "A guardrail stopped the build. Nothing is pushed, and Target keeps what it has.",
        failed: "A required source or the build failed. Nothing is pushed, and Target keeps what it has.",
        running: "The build is in progress.",
    },

    sourceStatus: {
        ok: "Called and processed successfully.",
        skipped: "An optional source failed and was left out of the build.",
        failed: "A required source failed, so the build failed.",
    },

    stats: {
        wouldBe: "What a real build would do with this result.",
        products: "Rows in the file.",
        invalid: "Rows dropped because a required value was missing or not allowed.",
        issues: "Values that were changed to fit Target's limits (with how many rows were affected), and rows that were dropped with the reason.",
        columns: "Columns in the file, including blank reserved ones.",
        fileSize: "Size of the CSV file.",
        calls: "HTTP requests made, including retries and pages.",
        pages: "Pages fetched across all calls.",
        orphans: "Variants that couldn't be linked to a product and were left out.",
        duplicates: "Products or variants that appeared more than once; the first one was kept.",
        documents: "Responses fetched: one per page, repeated call or dependent call.",
        filtered: "Products removed by the 'only keep products where' condition.",
        missingProductKey: "Products without a value for the key role; they were left out.",
        missingVariantKey: "Variants without a value for the key role; they were left out.",
        guardrails: "Safety checks a build must pass before it is pushed. The drop check compares with the last published run.",
        provenance: "Which source supplied each value.",
        previewLimit: "Reads only this many products from each primary source, so the preview is quicker. Sources called once per product follow them; sources that list a whole catalogue are still read in full. Guardrails are only checked when all products are previewed.",
        variantsTable: "Every variant after merging, with its fields. Hover a value to see which source supplied it.",
        csvValues: "The row exactly as it's written to the file.",
        rows: "The first rows of the file. Click a row to see its variants and where each value came from.",
        unmatched: "Enrichment records that didn't match any product and were ignored.",
        trigger: "What started the build: publish, schedule or manual (Run now).",
        configVersion: "The published configuration version the build used.",
        duration: "How long the build took.",
        rollback: "Not available: Stride doesn't keep the files that runs build, so there is nothing to roll back to.",
        download: "Not available: Stride doesn't keep the files that runs build. Download a preview's CSV from the Preview tab instead.",
        fill: "The share of rows with a value in each column.",
    },

    delivery: {
        methods: {
            api: "After every successful build, Stride saves every product straight into Target with the Recommendations API (Save entities). No Target feed is needed, and changes reach Target as soon as the build finishes. Needs an Adobe IO integration with Adobe Target enabled.",
            url: "Stride hosts the CSV at a secret URL. You create a URL feed in Target (Recommendations → Feeds) and Target downloads the file on its own schedule: daily, weekly or every two weeks.",
        },
        connection: "The Adobe IO integration (Setup → Adobe IO) whose credentials and Target tenant are used. It needs Adobe Target enabled, a tenant name, and a product profile that can edit Target.",
        environment: "The Target environment (host group) the products are saved to. Target only recommends products from the environment of the host the page belongs to.",
        zeroInventory: {
            send: "Saved with inventory 0. They stay in Target for as long as your sources list them; the inventory > 0 rule in your criteria stops them being recommended.",
            delete: "Deleted from this environment. They're saved again when they're back in stock.",
            skip: "Not sent at all. Target keeps whatever it last received for them, possibly in stock, until they expire 61 days later.",
        },
        pushStatus: {
            pushed: "Every product was saved (and products with 0 inventory deleted, if set).",
            partial: "Some products were saved; others were rejected or a call failed. Every product is sent again on the next build.",
            failed: "Nothing could be saved, e.g. the credentials, permissions or environment are wrong. Target keeps what it had.",
            running: "The push is in progress.",
        },
        counts: {
            saved: "Products Target created or updated.",
            zeroInventory: "Products in the file with inventory 0.",
            deleted: "Products with 0 inventory deleted from Target.",
            skipped: "Rows the API can't take, e.g. without a name or category. Fix them in CSV columns.",
            rejected: "Products Target refused. The reasons are listed below.",
        },
    },

    schedule: {
        url: "Paste this into Adobe Target (Recommendations → Feeds → URL). It contains a secret token, so anyone with it can read the file.",
        rotate: "Creates a new URL and stops the old one working immediately. Update Target afterwards.",
        enabled: "Paused feeds aren't rebuilt or pushed.",
        interval: "How often the feed is rebuilt and pushed to Target.",
        minProducts: "A build with fewer products than this is held.",
        maxDrop: "A build that loses more than this share of products compared with the last published run is held.",
        maxInvalid: "A build where more than this share of rows are invalid is held.",
        strictIds: "Target only accepts letters, digits, - and _ in ids.",
        bom: "A byte order mark at the start of the file. Only needed if Target shows garbled characters.",
        alerts: "Who is emailed when a build is held or fails, or a push to Target fails or is incomplete.",
    },

    role: {
        name: "Shown in the Sources list.",
        id: "Used by dependent calls and lookups to refer to this source.",
        role: "Primary sources create products. Enrichment sources only add to products that already exist.",
        matchOn: "How enrichment records are matched to products already in the feed.",
        required: "When on, a failure stops the build and nothing is pushed. When off, the build continues without this source.",
        minRecords: "Finding fewer products than this counts as a failure (default 1), e.g. when an API returns an empty list by mistake.",
    },
};
